import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { auth, db } from "../services/firebase";
import { buildWebApp } from "../services/functionsClient";
import { logUiEvent } from "../services/analytics";

const DEFAULT_CANVAS = `{
  "blocks": [
    { "type": "hero", "headline": "Welcome to your demo", "subhead": "Wire agents directly to UI blocks." },
    { "type": "form", "title": "Try the endpoint", "fields": ["name", "city"] },
    { "type": "results", "title": "Latest output" }
  ]
}`;

function BuildWebUiDevPage() {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [canvas, setCanvas] = useState(DEFAULT_CANVAS);
  const [apis, setApis] = useState([]);
  const [selectedApis, setSelectedApis] = useState([]);
  const [webApps, setWebApps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedId, setSavedId] = useState("");
  const [idea, setIdea] = useState("");
  const [aiStatus, setAiStatus] = useState("");
  const [aiError, setAiError] = useState("");
  const [appId, setAppId] = useState(() => uuidv4());
  const [iframeSrcDoc, setIframeSrcDoc] = useState("");
  const [model, setModel] = useState("gpt-5-mini-2025-08-07");
  const userId = auth.currentUser?.uid;

  const parsedCanvas = useMemo(() => {
    try {
      return JSON.parse(canvas || "{}") || {};
    } catch (err) {
      return null;
    }
  }, [canvas]);

  const modelOptions = useMemo(
    () => [{ value: "gpt-5-mini-2025-08-07", label: "gpt-5-mini-2025-08-07" }],
    []
  );

  const loadApis = async () => {
    const snapshot = await getDocs(query(collection(db, "apiEndpoints"), orderBy("createdAt", "desc")));
    setApis(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  };

  const loadWebApps = async () => {
    const snapshot = await getDocs(query(collection(db, "webApps"), orderBy("createdAt", "desc")));
    setWebApps(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  };

  useEffect(() => {
    loadApis();
    loadWebApps();
  }, []);

  useEffect(() => {
    if (!userId || !appId) return undefined;

    const appRef = doc(db, "users", userId, "apps", appId);
    const unsubscribe = onSnapshot(
      appRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setDoc(appRef, { status: "idle" }, { merge: true });
          return;
        }

        const data = snapshot.data();
        const content = extractAssistantContent(data.code);

        if (content) {
          try {
            const parsed = JSON.parse(normalizeJson(content));
            setIframeSrcDoc(generateIframeContent(parsed));
            setAiStatus("Canvas updated from AI-generated code.");
            setAiError("");
          } catch (parseError) {
            setAiError(parseError.message || "Failed to parse generated code.");
          }
        }
      },
      (snapshotError) => {
        setAiError(snapshotError.message);
      }
    );

    return unsubscribe;
  }, [appId, userId]);

  const normalizeJson = (text) => {
    const trimmed = text.trim();
    const fenceMatch = trimmed.match(/```(?:json)?\n([\s\S]*?)```/i);
    return fenceMatch ? fenceMatch[1].trim() : trimmed;
  };

  const extractAssistantContent = (codeField) => {
    if (!codeField) return null;

    if (Array.isArray(codeField)) {
      const assistantMessage = [...codeField]
        .reverse()
        .find((item) => item.role === "assistant" && item.content);
      return assistantMessage?.content || null;
    }

    if (typeof codeField === "string") return codeField;
    if (typeof codeField === "object" && codeField.content) return codeField.content;
    return null;
  };

  const generateIframeContent = (code) => {
    if (!code) return "";

    const html = code.html || "";
    const css = code.css || "";
    const js = code.js || "";
    const apiBaseUrl = (process.env.REACT_APP_FUNCTION_BASE_URL || "").replace(/"/g, "\\\"");

    return `<!doctype html>
<html>
  <head>
    <style>${css}</style>
  </head>
  <body>
    ${html}
    <script>const API_BASE_URL = "${apiBaseUrl}";</script>
    <script type="module">
      try {
        ${js}
      } catch (e) {
        console.error("Error executing generated app:", e);
        const errorDiv = document.createElement('div');
        errorDiv.style.position = 'fixed';
        errorDiv.style.bottom = '10px';
        errorDiv.style.left = '10px';
        errorDiv.style.padding = '10px';
        errorDiv.style.backgroundColor = 'red';
        errorDiv.style.color = 'white';
        errorDiv.style.zIndex = '9999';
        errorDiv.textContent = 'Error in script: ' + e.message;
        document.body.appendChild(errorDiv);
      }
    </script>
  </body>
</html>`;
  };

  const generateWithAi = async () => {
    if (!userId) {
      setAiError("You must be signed in to use AI drafting.");
      return;
    }
    if (!idea.trim()) {
      setAiError("Describe the app or demo you want first.");
      return;
    }

    setAiError("");
    setAiStatus("Sending idea to builder agent...");
    try {
      const apiDocsForPrompt = (selectedApis.length ? apis.filter((api) => selectedApis.includes(api.id)) : apis).map(
        (api) => ({
          id: api.id,
          name: api.name,
          description: api.description,
          method: api.method || api.httpMethod || "POST",
          path: api.path || api.url || api.route || "",
        })
      );

      const appRef = doc(db, "users", userId, "apps", appId);
      await setDoc(
        appRef,
        {
          name: name || "Untitled WebUI",
          description: description || "",
          idea: idea.trim(),
          selectedApis,
          model,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await buildWebApp({
        userId,
        appId,
        userPrompt: idea.trim(),
        apiDocs: apiDocsForPrompt,
        model,
      });

      setAiStatus("Build requested. Listening for generated code...");
      logUiEvent("webui_dev_ai_generated", { appId });
    } catch (err) {
      console.error(err);
      setAiError(err.message || "Failed to draft with AI");
      setAiStatus("");
    }
  };

  const toggleApi = (id) => {
    setSelectedApis((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const saveWebApp = async () => {
    setSaving(true);
    setError("");
    try {
      const docRef = await addDoc(collection(db, "webApps"), {
        name: name || "Untitled WebUI",
        description: description || "",
        canvas: canvas || DEFAULT_CANVAS,
        selectedApis,
        createdAt: serverTimestamp()
      });
      setSavedId(docRef.id);
      setAppId(docRef.id);
      await loadWebApps();
    } catch (err) {
      setError(err.message || "Failed to save web UI");
    } finally {
      setSaving(false);
    }
  };

  const renderPreview = () => {
    if (!parsedCanvas || !parsedCanvas.blocks) {
      return <p className="muted">Canvas JSON should include a `blocks` array.</p>;
    }

    return (
      <div className="list">
        {parsedCanvas.blocks.map((block, idx) => (
          <div key={idx} className="list__item">
            <div className="badge">{block.type || "block"}</div>
            <p>{block.headline || block.title || block.subhead || "Untitled block"}</p>
            {block.fields && <p className="muted">Fields: {block.fields.join(", ")}</p>}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="grid" style={{ gap: "16px" }}>
      <div className="section-header">
        <h2 className="section-header__title">Build WebUI (Dev)</h2>
        <p className="muted">Compose a quick app surface and link it to the agents you drafted.</p>
      </div>

      <div className="panel">
        <div className="grid" style={{ gap: "12px", marginBottom: "12px" }}>
          <div>
            <label className="field-label">Describe the WebUI you want</label>
            <textarea
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              rows={3}
              placeholder="e.g., A demo that collects a city + date, calls my weather agent, and shows a summary card"
            />
          </div>
          <div className="section-header" style={{ alignItems: "flex-end" }}>
            <div style={{ width: "100%", marginBottom: "8px" }}>
              <label className="field-label">Model</label>
              <select value={model} onChange={(e) => setModel(e.target.value)} style={{ width: "100%" }}>
                {modelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <button className="button" type="button" onClick={generateWithAi}>
              Ask AI to draft
            </button>
            {aiStatus && <p className="muted" style={{ marginTop: "8px" }}>{aiStatus}</p>}
            {aiError && (
              <p className="muted" style={{ marginTop: "8px", color: "#b00020" }}>{aiError}</p>
            )}
          </div>
        </div>
        <div className="grid grid--two" style={{ gap: "12px" }}>
          <div>
            <label className="field-label">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Marketing site, admin UI..." />
          </div>
          <div>
            <label className="field-label">Description</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="How should this feel?" />
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="section-header" style={{ marginBottom: "12px" }}>
          <div className="section-header__title">Canvas</div>
          <p className="muted">
            AI-generated code is written to <code>users/{userId || "<userid>"}/apps/{appId}</code> and rendered
            below.
          </p>
        </div>
        <div className="panel" style={{ minHeight: "320px", padding: 0 }}>
          {iframeSrcDoc ? (
            <iframe
              title="Generated Canvas"
              srcDoc={iframeSrcDoc}
              style={{ border: "0", width: "100%", height: "480px" }}
            />
          ) : (
            <p className="muted" style={{ padding: "12px" }}>
              Ask AI to draft your canvas and the preview will render here.
            </p>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="grid grid--two" style={{ gap: "12px" }}>
          <div>
            <label className="field-label">Canvas JSON</label>
            <textarea value={canvas} onChange={(e) => setCanvas(e.target.value)} rows={12} className="code-block" />
            {!parsedCanvas && <p className="muted">Canvas must be valid JSON.</p>}
          </div>
          <div>
            <label className="field-label">Preview</label>
            <div className="panel" style={{ minHeight: "240px" }}>{renderPreview()}</div>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="grid grid--two" style={{ gap: "12px" }}>
          <div>
            <label className="field-label">Available agents</label>
            <div className="list" style={{ maxHeight: "200px", overflow: "auto" }}>
              {apis.map((api) => (
                <label key={api.id} className="list__item" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={selectedApis.includes(api.id)}
                    onChange={() => toggleApi(api.id)}
                    style={{ marginRight: "8px" }}
                  />
                  <div>
                    <div className="badge">{api.name || "Untitled"}</div>
                    <p className="muted">{api.description || "No description"}</p>
                  </div>
                </label>
              ))}
              {apis.length === 0 && <p className="muted">No agents created yet.</p>}
            </div>
          </div>
          <div>
            <label className="field-label">Saved WebUIs</label>
            <div className="list" style={{ maxHeight: "200px", overflow: "auto" }}>
              {webApps.map((app) => (
                <div key={app.id} className="list__item">
                  <div className="badge">{app.name || "Untitled"}</div>
                  <p className="muted">{app.description || "No description"}</p>
                  <Link to={`/app/${app.id}`} className="button secondary" style={{ marginTop: "4px" }}>
                    Open preview
                  </Link>
                </div>
              ))}
              {webApps.length === 0 && <p className="muted">Nothing saved yet.</p>}
            </div>
          </div>
        </div>

        <div className="section-header" style={{ marginTop: "12px" }}>
          <div className="section-header__title">Actions</div>
          <button className="button" onClick={saveWebApp} disabled={saving}>
            {saving ? "Saving..." : "Save WebUI"}
          </button>
        </div>
        {error && <p className="muted" style={{ color: "#b00020" }}>{error}</p>}
        {savedId && (
          <p className="muted">Saved. Share or open: <Link to={`/app/${savedId}`}>/app/{savedId}</Link></p>
        )}
      </div>
    </div>
  );
}

export default BuildWebUiDevPage;
